import React, { useState, useEffect, useRef } from 'react'
import GridLayout from 'react-grid-layout'
import { Camera, GridLayout as GridLayoutType } from '../types'
import LiveView from '../pages/LiveView'
import { cameraService } from '../services/api'
import { API_BASE } from '../config'
import Hls from 'hls.js'

const FullscreenHLSPlayer: React.FC<{ cameraId: number }> = ({ cameraId }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    let hls: Hls | null = null
    let destroyed = false
    const start = async () => {
      await fetch(`${API_BASE}/api/v1/hls/start/${cameraId}`, { method: 'POST' }).catch(() => {})
      if (destroyed || !videoRef.current) return
      const url = `${API_BASE}/hls/${cameraId}/playlist.m3u8?_=${Date.now()}`
      if (Hls.isSupported()) {
        hls = new Hls({ lowLatencyMode: true, liveSyncDurationCount: 3, maxBufferLength: 6, backBufferLength: 0 })
        hls.loadSource(url)
        hls.attachMedia(videoRef.current)
      } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        videoRef.current.src = url
      }
    }
    start()
    return () => {
      destroyed = true
      hls?.destroy()
    }
  }, [cameraId])
  return (
    <video
      ref={videoRef}
      autoPlay muted playsInline controls={false}
      style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
    />
  )
}

const VideoWall: React.FC = () => {

  const [cameras, setCameras] = useState<Camera[]>([])
  const [layout, setLayout] = useState<GridLayoutType[]>([])
  const [gridSize, setGridSize] = useState(4)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [fullscreenCam, setFullscreenCam] = useState<Camera | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    buildLayout()
  }, [gridSize, page, cameras])

  const loadData = async () => {
    const data = await cameraService.getCameras()
    setCameras(data || [])
    setLoading(false)
  }

  const camerasPerPage = gridSize * gridSize
  const totalPages = Math.ceil(cameras.length / camerasPerPage)

  const paginatedCameras = cameras.slice(
    page * camerasPerPage,
    (page + 1) * camerasPerPage
  )

  const buildLayout = () => {
    const newLayout = paginatedCameras.map((cam, index) => ({
      i: `camera-${cam.id}`,
      x: index % gridSize,
      y: Math.floor(index / gridSize),
      w: 1,
      h: 1
    }))
    setLayout(newLayout)
  }

  const getCamera = (id: string) => {
    const camId = parseInt(id.replace('camera-', ''))
    return cameras.find(c => c.id === camId)
  }

  const tileHeight = Math.floor(window.innerHeight / gridSize) - 20

  if (loading) return <div className="bg-black text-white p-10">Loading…</div>

  return (
    <div className="bg-black h-screen flex flex-col">

      {/* TOP BAR */}
      <div className="bg-gray-800 p-3 flex justify-between items-center">

        <div className="space-x-2">
          {[1,2,3,4,5,6,7,8].map(n => (
            <button
              key={n}
              onClick={() => { setGridSize(n); setPage(0) }}
              className="bg-gray-700 px-3 py-1 rounded text-white"
            >
              {n}x{n}
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-2 text-white">

          <button
            disabled={page === 0}
            onClick={() => setPage(p => Math.max(p - 1, 0))}
            className="bg-gray-700 px-3 py-1 rounded"
          >
            ◀
          </button>

          <span>Page {page + 1} / {totalPages || 1}</span>

          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))}
            className="bg-gray-700 px-3 py-1 rounded"
          >
            ▶
          </button>

        </div>

      </div>

      {/* GRID */}
      <div className="flex-1">

        <GridLayout
          layout={layout}
          cols={gridSize}
          rowHeight={tileHeight}
          width={window.innerWidth}
          isDraggable={false}
          isResizable={false}
        >

          {layout.map(item => {

            const camera = getCamera(item.i)

            if (!camera) return null

            const rtsp =
              camera.sub_stream ||
              camera.rtsp_url ||
              camera.main_stream ||
              ""

            return (
              <div
                key={item.i}
                className="bg-gray-900 rounded overflow-hidden relative cursor-pointer"
                onClick={() => setFullscreenCam(camera)}
              >

                {/* WEBRTC SUB STREAM */}
                <LiveView rtspUrl={rtsp} />

                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-xs text-white p-1">
                  {camera.name}
                </div>

              </div>
            )

          })}

        </GridLayout>

      </div>

      {/* FULLSCREEN */}
      {fullscreenCam && (
        <div className="fixed inset-0 bg-black z-50">
          <FullscreenHLSPlayer cameraId={fullscreenCam.id} />
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
            <span className="text-white text-base font-medium">{fullscreenCam.name}</span>
            <button
              className="pointer-events-auto text-white bg-black/50 hover:bg-black/80 rounded-full w-8 h-8 flex items-center justify-center text-lg leading-none"
              onClick={() => setFullscreenCam(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}

    </div>
  )
}

export default VideoWall
